"use client";
import React, { useState, useEffect } from "react";
import Link from "next/link";

const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const proteinUrl = "/protein/search";

  // Dropdown data
  const dropdownData: Record<string, (string | Record<string, string>)[]> = {
    "Browse-Data": [ { "ASFV Genome": `/asfv-genome` },
                     {"Proteins": `/protein`},
                     {"Antigens": `/antigen`},
                     {"Epitopes": `/epitope/all`}
    ],
    "Resources": [ { "Epitope Prediction Methods": `/instructions` },
                   { "Download": `/download` }
    ],
  };

  const toggleMenu = (): void => {
    setIsMenuOpen(!isMenuOpen);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent): void => {
      const target = e.target as HTMLElement;
      if (!target.closest(".dropdown") && !target.closest(".mobile-dropdown")) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const handleDropdownClick = (dropdown: string): void => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
  };

  // Generate URL from option name or object
  const getOptionSlug = (category: string, option: string | Record<string, string>): string => {
    if (typeof option === "string") {
      return `/${category.toLowerCase()}/${option.toLowerCase().replace(/ /g, "-")}`;
    }
    const key = Object.keys(option)[0];
    return option[key];
  };

  return (
    <header className="bg-gray-800 py-4 px-6 flex justify-between items-center relative">
      {/* Logo */}
<div className="text-white">
  <div className="text-2xl font-semibold">ASFV-EpiBase</div>
  <div className="text-xl max-w-5xl">A Comprehensive Epitope and Genome Resource for African Swine Fever Virus</div>
</div>

      {/* Mobile menu button */}
      <button
        className="md:hidden text-white hover:text-gray-400 focus:outline-none"
        onClick={toggleMenu}
        title="Toggle mobile menu"
        aria-label="Toggle mobile menu"
      >
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          {isMenuOpen ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          )}
        </svg>
      </button>

      {/* Desktop Navigation */}
      <nav className="hidden md:flex space-x-8 text-lg text-white">
        <Link href="/" className="hover:text-gray-400">Home</Link>
        {Object.keys(dropdownData).map((category) => (
          <div key={category} className="relative group dropdown">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDropdownClick(category.toLowerCase());
              }}
              className="flex items-center hover:text-gray-400 focus:outline-none"
            >
              {category.replace('-',' ')}
              <svg
                className={`ml-2 w-4 h-4 transition-transform ${
                  activeDropdown === category.toLowerCase() ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {activeDropdown === category.toLowerCase() && (
              <div className="absolute mt-2 w-64 bg-gray-700 rounded-md shadow-lg py-1 z-20">
                {dropdownData[category].map((option, index) => (
                  <Link
                    key={index}
                    href={getOptionSlug(category, option)}
                    className="block px-4 py-2 text-sm text-white hover:bg-gray-600"
                    onClick={() => setActiveDropdown(null)}
                  >
                    {typeof option === "string" ? option : Object.keys(option)[0]}
                  </Link>
                ))}
              </div>
            )}
          </div>
        ))}
        <Link href="/protein/blast" className="hover:text-gray-400">BLAST</Link>
      </nav>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="absolute top-16 left-0 right-0 bg-gray-800 z-10 md:hidden">
          <div className="flex flex-col px-6 py-4 space-y-4 text-lg text-white">
            <Link href="/" className="hover:text-gray-400" onClick={() => setIsMenuOpen(false)}>Home</Link>
            {Object.keys(dropdownData).map((category) => (
              <div key={category} className="relative mobile-dropdown">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDropdownClick(`mobile-${category.toLowerCase()}`);
                  }}
                  className="flex items-center hover:text-gray-400 focus:outline-none w-full"
                >
                  {category.replace('-',' ')}
                  <svg
                    className={`ml-2 w-4 h-4 transition-transform ${
                      activeDropdown === `mobile-${category.toLowerCase()}` ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {activeDropdown === `mobile-${category.toLowerCase()}` && (
                  <div className="mt-2 pl-4 flex flex-col space-y-2">
                    {dropdownData[category].map((option, index) => (
                      <Link
                        key={index}
                        href={getOptionSlug(category, option)}
                        className="block px-4 py-2 text-sm text-white hover:bg-gray-600"
                        onClick={() => {
                          setActiveDropdown(null);
                          setIsMenuOpen(false);
                        }}
                      >
                        {typeof option === "string" ? option : Object.keys(option)[0]}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <Link href="/protein/blast" className="hover:text-gray-400" onClick={() => setIsMenuOpen(false)}>BLAST</Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
